import React, { useState, useEffect } from 'react';
import {
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Stepper, 
  Step, 
  StepLabel, 
  CircularProgress 
} from '@mui/material';
import axios from 'axios';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { footerLinks } from '../Shared/footerLinks.jsx';
import { config } from '../../../config/settings.jsx';
import { steps } from '../../../config/steps.jsx';
import { api } from '../../../config/apis.jsx';
import { getTheme } from '../../../config/theme.jsx';

const RedirectFromBasiq = () => {
  const [isLoadingTheme, setIsLoadingTheme] = useState(false);
  const [theme, setTheme] = useState({});
  const [assetsPath, setAssetsPath] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRetry, setIsLoadingRetry] = useState(false);
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState('Checking your bank connection and authorising accounts');
  const [pageTitle, setPageTitle] = useState('Connecting...');
  const location = useLocation();
  const cdrid = localStorage.getItem('cdrid');
  const [stepperStep, setStepperStep] = useState(1);
  const [reconsentRequired, setReconsentRequired] = useState(false);
  const [logOut, setLogOut] = useState(false);
  const [newConsentLink, setNewConsentLink] = useState('');
  const [parentClub, setParentClub] = useState('');
  const queryParams = new URLSearchParams(location.search);
  const jobId = queryParams.get('jobId');

  useEffect(() => {
    // Check if jobId exists, if not show error
    if (!jobId) {
      console.error('No jobId found in URL parameters');
      setStatusMessage("No job ID found. Please return to Cents2Bills and try again");
      setPageTitle("Connection Failed");
      setReconsentRequired(true);
      return;
    }

    console.log('Job ID received:', jobId);
    
    const checkJobStatus = async (retries = 0) => {
      const maxRetriesStatus = 10;
      
      try {
        console.log(`Checking job status (attempt ${retries + 1})...`);
        const response = await axios.post(
          api.checkConnection,
          { jobId: jobId },
          { headers: { 'Content-Type': 'application/json', apikey: config.apikey, tenant: config.tenant } }
        );
        
        const status = response.status;
        console.log('Response status:', status, 'Data:', response.data);
        
        if (status === 200) {
          setStepperStep(2);
          setPageTitle('Finding Accounts!');
          setStatusMessage('Successfully connected - we are now retrieving your accounts.');
          const cdrid = response.data.cdrid;

          const retrieveAccounts = await axios.post(
            api.retrieveAccounts,
            { cdrid: cdrid },
            { headers: { 'Content-Type': 'application/json', apikey: config.apikey, tenant: config.tenant } }
          );
          
          if (retrieveAccounts.status === 200) {
            setPageTitle('Success!');
            setStatusMessage('Accounts have successfully been retrieved.');
            navigate('/roundup-accounts', { state: { cdrid: cdrid } });
          }
        } else if (status === 202) {
          setPageTitle('Checking!');
          setStatusMessage('Receiving communications from your bank. This might take a little while.');
          
          if (retries < maxRetriesStatus) {
            setTimeout(() => checkJobStatus(retries + 1), 5000);
          } else {
            setStatusMessage("Connection timeout. Please return to Cents2Bills and try again");
            setPageTitle("Accounts Retrieval Failed");
            setReconsentRequired(true);
          }
        }
      } catch (error) {
        console.error('Error checking job status:', error);
        
        if (retries < maxRetriesStatus) {
          setStatusMessage("We are still waiting for your bank to respond. This might take a little while.");
          setPageTitle("Connection Pending");
          setTimeout(() => checkJobStatus(retries + 1), 5000);
        } else {
          setStatusMessage("Unable to verify connection. Please try again.");
          setPageTitle("Connection Failed");
          setReconsentRequired(true);
        }
      }
    };

    // Start checking after a short delay (3 seconds instead of 20)
    const initialDelay = setTimeout(() => {
      checkJobStatus();
    }, 3000);

    return () => clearTimeout(initialDelay);
  }, [jobId, navigate]);

  
  const handleRetry = async () => {
    setIsLoadingRetry(true);
    try {
      const response = await axios.post(api.consent,
      {cdrid: cdrid},
      { headers: { 'Content-Type': 'application/json',
        apikey: config.apikey,
            tenant: config.tenant
       } }); // Replace with your actual API endpoint
      const consentLink = response.data.consentLink;
      window.location.href = consentLink // Adjust based on your API response structure
    } catch (error) {
      console.error('Error creating consent Link:', error);
    } finally {
      setIsLoadingRetry(false)
    }
  };

  const logOutButton = async () => {
    navigate('/logout');
  };

  return (
    <ThemeProvider theme={getTheme}>
    <Container component="main" maxWidth="xs">
    {isLoadingTheme ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mt: 20 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
 
      <Box display="flex" flexDirection="column" alignItems="center" mt={4} p={2} >
      <Grid container justifyContent="center" mb={2}>
    
         <Box display="flex" flexDirection="column" alignItems="center" mt={4} mb={6} style={{ width: '100%'}} >
        <Stepper activeStep={stepperStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
        </Box>
      
      <Typography variant="h4" component="h2" align="center" mt={2} sx={{fontWeight: 'bold'}} gutterBottom>
      {pageTitle}
      </Typography>
      
      <Box display="flex" flexDirection="column" alignItems="center" mt={4} mb={16} style={{ width: '100%'}}>
      <Typography variant="body2" component="h2" align="center"  gutterBottom>
       {statusMessage}
      </Typography>
      {reconsentRequired && (
                <Button
                onClick={handleRetry}
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2, borderRadius: 3, width: "100%"  }}
                disabled={isLoadingRetry}
    
                      
              >
                {isLoadingRetry ? 'Loading...' : 'Retry'}
             
              </Button>
              )}
        {logOut && (
                <Button
                onClick={logOutButton}
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2, borderRadius: 3, width: "100%"  }}
              
    
                      
              >
              Log In
             
              </Button>
              )}
   
      </Box>

      
    
      </Grid>
      



                    
        {footerLinks}
        </Box></>)}
    </Container></ThemeProvider>
  );
};

export default RedirectFromBasiq;

export {};